import { html, LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { rasterizeToFirstFrame } from '../../base/package/util/game-asset-url';
import type { HotGameWin } from '../ui-activity-lane/ui-activity-lane';
import { BIG_WIN } from '../ui-win-card/ui-win-card';
import style from './ui-hot-game.css?inline';
import '../ui-rolling-number/ui-rolling-number';
import '../ui-screen-reader-only/ui-screen-reader-only';
import '../ui-win-history/ui-win-history';

const CName = 'ui-hot-game';

@customElement(CName)
export class UiHotGame extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: String }) gameId: string;
  @property({ type: Boolean }) isViral = false;
  @property({ type: Number }) playerNumber: number;
  @property({ type: Number }) fireRate: number;
  @property({ type: Array }) wins: HotGameWin[];
  @property({ type: Boolean, reflect: true, attribute: 'reduce-motion' }) reduceMotion = false;

  @state() _hasNewBigWin = false;
  @state() _oldWinIds: string[];
  @state() _wins: HotGameWin[];
  @state() _img: string | null;
  @state() _title: string;

  protected willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('wins')) {
      const bigWins = this.wins.filter((win) => win.winAmount >= BIG_WIN);
      if (bigWins.length && this._oldWinIds?.length) {
        this._hasNewBigWin = bigWins.every((win) => !this._oldWinIds.includes(win.id));
      }
      this._oldWinIds = this.wins.map((win) => win.id);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._getGame(this.gameId).then((result) => {
      if (result) {
        this._title = result.title;
        if (this.reduceMotion && result.img) {
          // Freeze animated images to their first frame
          rasterizeToFirstFrame(result.img).then((staticSrc) => {
            if (!this.isConnected) return;
            this._img = staticSrc;
          });
        } else {
          this._img = result.img;
        }
      }
    });
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('reduceMotion') && this.reduceMotion && this._img) {
      rasterizeToFirstFrame(this._img).then((staticSrc) => {
        if (!this.isConnected) return;
        this._img = staticSrc;
      });
    }
  }

  render() {
    const animatedUrl = new URL('/assets/ui-core/flame-36.webp', import.meta.url).href;
    const staticUrl = new URL('/assets/ui-core/flame-36-static.webp', import.meta.url).href;
    const assetUrl = this.reduceMotion ? staticUrl : animatedUrl;
    const classes = classMap({
      'hot-game': true,
      'hot-game--viral': this.isViral,
      'hot-game--big': this._hasNewBigWin,
    });

    return html`
      <div class=${classes} data-state=${window.$app.$t.get(I18n.activityLane.game.state.viral)}>
        ${this.isViral ? html`<ui-screen-reader-only>${window.$app.$t.get(I18n.activityLane.game.state.viral)}</ui-screen-reader-only>` : nothing}
        <div class="hot-game__header">
          <div class="hot-game__title">
            <ui-screen-reader-only>${this.playerNumber} ${window.$app.$t.get(I18n.activityLane.activePlayers)}</ui-screen-reader-only>
            <ui-rolling-number .value=${this.playerNumber} .reduceMotion=${this.reduceMotion} aria-hidden="true"></ui-rolling-number>
          </div>
          <div class="hot-game__subtitle" aria-hidden="true">${window.$app.$t.get(I18n.activityLane.activePlayers)}</div>
          <div class="hot-game__flame-rate">
            <ui-screen-reader-only>${this.fireRate} rate</ui-screen-reader-only>
            ${repeat(
              Array.from({ length: this.fireRate }),
              (_, key) => key,
              () => html`
                <div class="hot-game__flame-wrapper" aria-hidden="true">
                  <img class="hot-game__flame-icon" src=${assetUrl} alt="${window.$app.$t.get(I18n.a11y.image.flameIcon)}" />
                </div>
              `,
            )}
          </div>
        </div>
        <div class="hot-game__thumbnail">
          ${
            this._img && this._img !== ''
              ? html`<img
                  @load="${this._onImageLoad}"
                  alt=${this._title}
                  class="hot-game__thumbnail-image"
                  crossorigin="anonymous"
                  fetchpriority="high"
                  loading="eager"
                  src=${this._img}
                />`
              : html`<div class="hot-game__placeholder"></div>`
          }
        </div>
        <div class="hot-game__wins">
          <ui-win-history .items=${this.wins} .reduceMotion=${this.reduceMotion}></ui-win-history>
        </div>
      </div>
    `;
  }

  private _onImageLoad(event: Event) {
    (event.target as HTMLImageElement).classList.add('loaded');
  }

  private async _getGame(gameId: string) {
    return await window.$app.content
      .getGame(gameId)
      .catch((err) => window.$app.logger.warn(`Error fetching game info '${gameId}'`, err));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiHotGame;
  }
}
